'use es6';

import PropTypes from 'prop-types';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { Button } from 'foundations-components/transitional/components/Button';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { H2 } from 'foundations-components/transitional/components/H2';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import { useHideDialog } from '../../../dialog/ducks';
import { useTracker } from '../../../utils/hooks/useTracker';
import { EVENTS } from '../../../utils/usageTracker';
import { useUserRoleForReport, useUserRoleFromScopes } from '../../../utils/trackingUtils';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { useMutation } from 'data-fetching-client';
import { RESTORE_REPORT } from '../../../data/requests/report';
import { ReportActionsFloatingAlertStore } from 'reporting-action-components/shared/ReportActionsFloatingAlertStore';
import { Set as ImmutableSet } from 'immutable';
import { getId as getReportId } from '../../../data/schemas/reportSchema';
import PortalIdParser from 'PortalIdParser';
import { useHasReportingAdminAccess } from '../../../access/scopes/hooks/useHasScope';
import { FETCH_REPORT_COUNTS } from '../../../data/requests/reportCounts';
import { getDefaultReportActionEventProperties } from '../../report-action-wrapper';
import { noop } from '../../shared/noop';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const RestoreReportModal = ({
  appUsageTracker,
  reports,
  onRestoreReportSuccess = noop
}) => {
  const hideDialog = useHideDialog();
  const isAdmin = useHasReportingAdminAccess();
  const usageTracker = useTracker(appUsageTracker);
  const count = reports.length;
  const firstReport = !reports.isEmpty() && reports.first();
  const firstReportName = firstReport && firstReport.get('name');
  const PORTAL_ID = PortalIdParser.get();
  const reportListUrl = `/reports-list/${PORTAL_ID}`;
  const userAssetRole = useUserRoleForReport(firstReport);
  const userReportingRole = useUserRoleFromScopes();
  const countDependentTrackingProps = count === 1 ? Object.assign({
    role: userAssetRole
  }, getDefaultReportActionEventProperties(firstReport)) : {
    role: userReportingRole
  };
  const reportIds = reports.reduce((memo, report) => memo.add(getReportId(report)), ImmutableSet()).toJS();
  const [restoreReportMutation] = useMutation(RESTORE_REPORT, {
    variables: {
      reportIds
    },
    onCompleted: () => {
      if (usageTracker) {
        usageTracker.track(EVENTS.restoreAsset, Object.assign({
          action: 'restore',
          actionType: 'save',
          assetType: 'report',
          count,
          isAdmin
        }, countDependentTrackingProps));
      }
      onRestoreReportSuccess();
      ReportActionsFloatingAlertStore.addAlert({
        message: /*#__PURE__*/_jsx(_Fragment, {
          children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
            message: "reporting-action-components.actions.restore.modal.success.message",
            options: {
              name: firstReportName,
              count,
              link: reportListUrl
            }
          })
        }),
        type: 'success',
        'data-test-id': 'report-save-success-alert'
      });
    },
    onError: error => {
      console.error(error);
      ReportActionsFloatingAlertStore.addAlert({
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "reporting-action-components.actions.restore.modal.failure",
          options: {
            count
          }
        }),
        type: 'danger'
      });
    },
    refetchQueries: () => {
      return [{
        query: FETCH_REPORT_COUNTS
      }];
    }
  });
  const handleConfirm = () => {
    restoreReportMutation();
    hideDialog();
  };
  return /*#__PURE__*/_jsxs(Modal, {
    "data-test-id": "report-restore-modal",
    children: [/*#__PURE__*/_jsxs(DialogHeader, {
      children: [/*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: hideDialog
      }), /*#__PURE__*/_jsx(H2, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "reporting-action-components.actions.restore.modal.message",
          options: {
            count,
            name: firstReportName
          }
        })
      })]
    }), /*#__PURE__*/_jsx(DialogBody, {
      children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "reporting-action-components.actions.restore.modal.description",
        options: {
          count
        }
      })
    }), /*#__PURE__*/_jsxs(DialogFooter, {
      children: [/*#__PURE__*/_jsx(Button, {
        use: "primary",
        onClick: handleConfirm,
        "data-test-id": "report-save-confirm",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "reporting-action-components.actions.restore.modal.confirm",
          options: {
            count
          }
        })
      }), /*#__PURE__*/_jsx(Button, {
        use: "secondary",
        onClick: hideDialog,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "reporting-action-components.actions.restore.modal.reject"
        })
      })]
    })]
  });
};
RestoreReportModal.propTypes = {
  appUsageTracker: PropTypes.object.isRequired,
  reports: ImmutablePropTypes.set.isRequired,
  onRestoreReportSuccess: PropTypes.func
};
export default RestoreReportModal;